import React, { useState, useEffect, useMemo } from "react";
import {
  Save,
  Building2,
  Users,
  Mail,
  Link as LinkIcon,
  Image as ImageIcon,
  FileEdit,
  Globe,
  PenTool,
  FileText,
  Search,
  ClipboardList,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  ChevronDown,
  User,
  Briefcase,
  Calendar,
  Tags,
  DollarSign,
  FileQuestion,
  Settings,
  Mail as MailIcon,
  Layout,
  ListChecks,
  Upload,
  GripVertical,
  ArrowLeft,
  ChevronUp,
  Eye,
  Trash2,
  PoundSterling,
  Loader2,
} from "lucide-react";
import { Editor } from "@tinymce/tinymce-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormBuilder } from "../FormBuilder/FormBuilder";
import { saveDraft } from "../../utils/draftHelpers";
import { generateSlug, useDebounce } from "../../utils/helpers";
import FormFieldsCard from "../FormBuilder/FormFieldsCard";
import slugService from "../../services/slugService";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MdDelete } from "react-icons/md";
import * as LucideIcons from "lucide-react";
import FormBuilderColumns from "../FormBuilder/FormBuilderColumns";
import { v4 as uuidv4 } from "uuid";
import { TIMEZONES } from "@/config";
import { useNavigate, useParams, useLocation, data } from "react-router-dom";
import vacanciesData from "../../data/vacancies.json"; // Assuming you have a vacancies data file
import {
  usePersonalForms,
  useJobSpecificForms,
} from "@/hooks/queries/useForms";
import { useCreateVacancy, useUpdateVacancy } from "@/hooks/mutations/vacancy";
import { useVacancyById } from "@/hooks/queries/usevacancy";
import {
  useVacancyStatusTemplates,
  useVacancyStatusTemplateById,
} from "@/hooks/queries/useVacancyStatusTemplates";

import useToastStore from "@/components/Common/Toast/toastStore";
import ConfirmationDialog from "@/components/Common/ConfirmationDialog";
import Loader from "@/components/Loader";
import isEqual from "lodash/isEqual";
// const params = useParams();
import { useCampaignByCompanyId } from "@/hooks/queries/campaigns";
import { useBullhornHotlist } from "@/hooks/queries/useBullhorn";
// import Dropdown from "../ui/Dropdown";
import Dropdown from "@/components/ui/Dropdown";

import PageHeader from "@/components/Common/PageHeader";
import VacancyPreviewModal from "./VacancyPreviewModal";

const emailTemplates = [
  { id: 1, name: "Standard Interview Invitation" },
  { id: 2, name: "Technical Assessment Invitation" },
  { id: 3, name: "Final Round Interview" },
];

// Define sequence steps
const defaultSequenceSteps = [
  // Phone Screen
  {
    id: "phone_screen",
    title: "Phone Screen",
    color: "#FFD700",
  },

  // Video Interview
  {
    id: "video_interview",
    title: "Video Interview",
    color: "#FF6347",
  },

  //Long list
  {
    id: "longlist",
    title: "Long List",
    color: "#FF6347",
  },

  //Short List
  {
    id: "shortlist",
    title: "Short List",
    color: "#FF6347",
  },

  //Assessment Center
  {
    id: "assessment_center",
    title: "Assessment Center",
    color: "#FF6347",
  },
];

const iconOptions = Object.keys(LucideIcons).map((iconId) => ({
  id: iconId,
  icon: LucideIcons[iconId],
}));

const colorOptions = [
  { id: "red", color: "#FF0000" },
  { id: "green", color: "#00FF00" },
  { id: "blue", color: "#0000FF" },
  { id: "yellow", color: "#FFFF00" },
  { id: "orange", color: "#FFA500" },
  { id: "purple", color: "#800080" },
  // Add more colors as needed
];

const { showToast } = useToastStore;

// Add this helper function at the top (or import it from a utilities file)
const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  // Ensure month and day are two digits
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to validate slug format
const validateSlugFormat = (slug) => {
  if (!slug) return true; // Empty slug is considered valid

  // Check for valid slug pattern: lowercase letters, numbers, single dashes
  // Replace spaces with dashes
  const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  return slugRegex.test(slug);
};

function VacancyForm(setActiveTab) {
  const { campaignId, vacancyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(vacancyId);
  const [vacancyDetails, setVacancyDetails] = useState({});

  console.log("location.state", location.state);
  // Retrieve campaign title passed from Campaigns.jsx
  const [campaign, setCampaign] = useState(location.state?.campaign || null);

  // Add selectedCompany state
  const [selectedCompany, setSelectedCompany] = useState(
    campaign?.company || null,
  );

  // Get campaignId from URL parameters
  const [currentPage, setCurrentPage] = useState("vacancy");
  // Fetch vacancy details using custom hook useVacancyById
  // - vacancyId is passed from URL parameters
  // - data: contains the fetched vacancy details
  // - isLoading: boolean indicating if data is still loading
  // - isError: boolean indicating if there was an error fetching data
  const {
    data: _vacancyDetails,
    isLoading: isVacancyLoading,
    isError: isVacancyError,
  } = useVacancyById(vacancyId);

  useEffect(() => {
    if (_vacancyDetails && !isEqual(_vacancyDetails?.vacancy, vacancyDetails)) {
      console.log("_vacancyDetails", _vacancyDetails);
      setVacancyDetails(_vacancyDetails?.vacancy);
    }
  }, [_vacancyDetails]);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    slugManual: false, // Track if slug was manually edited
    slugGenerating: false, // Track if slug is being generated
    slugValid: true, // Track if slug format is valid
    slugAvailable: true, // Track if slug is available
    slugMessage: "", // Track API response message
    description: "",
    applicationType: "",
    campaignId: "",
    recruiters: [],
    requiresCoverLetter: true,
    requiresCV: true,
    isPicEnabled: false,
    sms: false,
    foundationMembers: false,
    foundationMemberOption: null,
    formTemplate: "",
    startingSalary: "",
    openDate: null,
    closeDate: null,
    placesAvailable: "",
    customQuestions: [],
    tags: [],
    selectedEmailTemplate: "",
    emailCustomization: "",
    preCampaignText: "",
    postApplicationText: "",
    campaignEndMessage: "",
    customProcess: false,
    customFields: {
      job: [],
      personal: [],
    },
    scorecardOptions: [],
    smartRules: [],
    cover_letter_label: "",
    resume_label: "", // Add this new field for resume label
    bullhorn_contact_id: "",
    timezone: "",
    startTime: "",
    endTime: "",
    aiSummaryPrompt: "",
    aiSummaryFields: [],
    
  });
  const {
    data: { campaigns = [], pagination = {} } = {},
    isLoading = false,
    isError = false,
  } = useCampaignByCompanyId(campaignId, {
    page: 1,
    per_page: 50,
    searchTerm: "",
  });

  const [showFormBuilder, setShowFormBuilder] = useState(false);

  // Remove the old single-form selections and use arrays for multiple selections
  const [selectedPersonalForms, setSelectedPersonalForms] = useState([]);
  const [selectedJobSpecificForms, setSelectedJobSpecificForms] = useState([]);

  const [selectedFormType, setSelectedFormType] = useState(null);
  const [showFormTypeDetails, setShowFormTypeDetails] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [expandedPageElement, setExpandedPageElement] = useState(null);
  const [sequenceSteps, setSequenceSteps] = useState(defaultSequenceSteps);
  const [draggedStep, setDraggedStep] = useState(null);
  const [applicationFormTypes, setApplicationFormTypes] = useState([]);
  const [savedForms, setSavedForms] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [customProcessEnabled, setCustomProcessEnabled] = useState(true);
  const [newStepName, setNewStepName] = useState("");
  const [selectedColor, setSelectedColor] = useState("#FF0000"); // Default color
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  // const [newRecruiter, setNewRecruiter] = useState("");

  // New state variable for the personal form that should open in the builder
  const [showPersonalFormBuilder, setShowPersonalFormBuilder] = useState(false);
  const [showJobSpecificFormBuilder, setShowJobSpecificFormBuilder] =
    useState(false);
  const [selectedPersonalFormForBuilder, setSelectedPersonalFormForBuilder] =
    useState(null);
  const [
    selectedJobSpecificFormForBuilder,
    setSelectedJobSpecificFormForBuilder,
  ] = useState(null);

  const [personalFormPlaceholder, setPersonalFormPlaceholder] = useState(
    "Select a Personal Form",
  );
  const [jobSpecificFormPlaceholder, setJobSpecificFormPlaceholder] = useState(
    "Select a Job Specific Form",
  );

  const [pOrderStatus, setPOrderStatus] = useState([]);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Confirmation dialog state for status deletion
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);

  // Fetch personal forms
  const { data: personalForms = [], isLoading: isLoadingPersonalForms } =
    usePersonalForms();

  // Fetch job specific forms
  const { data: jobSpecificForms = [], isLoading: isLoadingJobSpecificForms } =
    useJobSpecificForms();

  // Fetch vacancy status templates
  const { data: templatesResponse, isLoading: isLoadingTemplates } =
    useVacancyStatusTemplates();
  const templates = templatesResponse?.data || [];

  // Fetch selected template details
  const { data: selectedTemplateResponse } = useVacancyStatusTemplateById(
    selectedTemplateId,
    {
      enabled: !!selectedTemplateId,
    },
  );
  const selectedTemplate =
    selectedTemplateResponse?.data || selectedTemplateResponse;

  const { mutate: createVacancy, isPending: isCreatingVacancy } =
    useCreateVacancy();
  const { mutate: updateVacancy, isPending: isUpdatingVacancy } =
    useUpdateVacancy();

  const {
    data: hotlistOptions = [],
    isLoading: isHotlistLoading,
    isError: isHotlistError,
  } = useBullhornHotlist({
    enabled: formData.foundationMembers,
  });

  const selectedHotlistOption = useMemo(() => {
    if (!formData.foundationMemberOption) return null;

    return (
      hotlistOptions.find(
        (option) => option.value === String(formData.foundationMemberOption),
      ) ?? {
        value: String(formData.foundationMemberOption),
        label: String(formData.foundationMemberOption),
      }
    );
  }, [formData.foundationMemberOption, hotlistOptions]);

  // Load saved forms on mount
  useEffect(() => {
    const forms = JSON.parse(localStorage.getItem("savedForms") || "[]");
    setSavedForms(forms);
    // setPersonalForms(forms.filter(form => form.type === 'Personal'));
    // setJobSpecificForms(forms.filter(form => form.type === 'Job Specific'));
  }, []);

  // Populate application form types with saved forms
  useEffect(() => {
    const formTypes = savedForms.map((form) => ({
      id: form.id,
      name: form.name,
      changes: form.fields.map((field) => field.label), // Assuming fields have a label
    }));
    setApplicationFormTypes(formTypes);
  }, [savedForms]);

  useEffect(() => {
    if (campaignId) {
      setFormData((prev) => ({
        ...prev,
        campaignId: campaignId, // Set the campaignId in formData
      }));
    }
  }, [campaignId, campaign]);

  // Update selectedCompany when campaign changes
  useEffect(() => {
    console.log("campaign", campaign);
    console.log("campaign?.company", campaign?.company);
    if (campaign?.company) {
      setSelectedCompany(campaign.company);
    }
  }, [campaign]);

  // Auto-generate slug when title is set but slug is empty (for new forms)
  useEffect(() => {
    if (formData.title && !formData.slug && !formData.slugManual) {
      const newSlug = generateSlug(formData.title);
      const isValid = validateSlugFormat(newSlug);
      setFormData((prev) => ({
        ...prev,
        slug: newSlug,
        slugValid: isValid,
        slugAvailable: true, // Assume available for auto-generated slugs
        slugMessage: "", // Clear message for auto-generated slugs
      }));
    }
  }, [formData.title, formData.slug, formData.slugManual]);

  // Set campaign from campaigns data if not already set (for create mode)
  useEffect(() => {
    console.log("campaigns data:", campaigns);
    console.log("location.state:", location.state);
    console.log("campaignId:", campaignId);
    console.log("isEditMode:", isEditMode);

    if (
      !isEditMode &&
      campaigns.length > 0 &&
      !campaign &&
      location.state?.campaignId
    ) {
      const selectedCampaign = campaigns.find(
        (c) => c.id.toString() === location.state.campaignId.toString(),
      );
      console.log("selectedCampaign:", selectedCampaign);
      if (selectedCampaign) {
        setCampaign(selectedCampaign);
      }
    }
  }, [campaigns, isEditMode, campaign, location.state?.campaignId]);

  // Debounced slug availability check when manually entered
  const debouncedSlugCheck = useDebounce(async (slugValue) => {
    if (slugValue && formData.slugManual) {
      try {
        setFormData((prev) => ({
          ...prev,
          slugGenerating: true,
          slugMessage: "", // Clear previous message
        }));

        // Check slug availability using the appropriate endpoint
        const result = await slugService.checkSlugAvailability(
          slugValue,
          isEditMode ? vacancyId : null,
        );

        // Check if the API response indicates success
        // A successful response typically means the slug is available (no conflicts)
        // If there are errors in the response, it means the slug is not available
        // Also check if the response has an error status or contains error messages
        const hasErrors =
          result.errors ||
          result.status === "error" ||
          result.success === false ||
          result.message?.toLowerCase().includes("taken") ||
          result.message?.toLowerCase().includes("already");
        const isSuccess =
          !hasErrors &&
          (result.success === true ||
            result.status === "success" ||
            (!result.errors &&
              !result.message?.toLowerCase().includes("taken") &&
              !result.message?.toLowerCase().includes("already")));

        // Extract message from various possible locations in the response
        let message = "";
        if (
          result.errors &&
          result.errors.slug &&
          result.errors.slug.length > 0
        ) {
          // Priority: errors.slug array (most specific error message)
          message = result.errors.slug[0];
        } else if (result.errors && typeof result.errors === "string") {
          // Handle case where errors is a string
          message = result.errors;
        } else if (result.message) {
          // Fallback: direct message field
          message = result.message;
        } else if (result.msg) {
          // Fallback: msg field
          message = result.msg;
        } else if (isSuccess) {
          // Default success message
          message = "Slug is available";
        }

        if (isSuccess) {
          // API response is OK - show tick
          setFormData((prev) => ({
            ...prev,
            slugGenerating: false,
            slugValid: validateSlugFormat(slugValue),
            slugAvailable: true,
            slugMessage: message,
          }));
        } else {
          // API response is not OK - show X and message
          setFormData((prev) => ({
            ...prev,
            slugGenerating: false,
            slugValid: validateSlugFormat(slugValue),
            slugAvailable: false,
            slugMessage: message,
          }));
        }
      } catch (error) {
        console.log("Slug API Error:", error); // Debug log
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to check slug availability";
        setFormData((prev) => ({
          ...prev,
          slugGenerating: false,
          slugValid: validateSlugFormat(prev.slug), // Re-validate current slug
          slugAvailable: false,
          slugMessage: errorMessage,
        }));
      }
    }
  }, 2000); // 2 seconds delay

  // console.log(
  //   "selectedJobSpecificFormForBuilder",
  //   selectedJobSpecificFormForBuilder
  // );
  // console.log("selectedPersonalFormForBuilder", selectedPersonalFormForBuilder);
  useEffect(() => {
    if (isEditMode && vacancyDetails) {
      setFormData({
        title: vacancyDetails.title || "",
        slug: vacancyDetails.slug || "",
        slugManual: false, // Reset manual flag when loading existing data
        slugGenerating: false, // Reset generating flag when loading existing data
        slugValid: validateSlugFormat(vacancyDetails.slug || ""), // Validate existing slug
        slugAvailable: true, // Assume available when loading existing data
        slugMessage: "", // Clear message when loading existing data
        description: vacancyDetails.description || "",
        applicationType: vacancyDetails.applicationType || "",
        campaignId: vacancyDetails.campaign_id
          ? String(vacancyDetails.campaign_id)
          : "",
        requiresCoverLetter:
          vacancyDetails.cover_letter_enable !== undefined
            ? vacancyDetails.cover_letter_enable
            : true,
        cover_letter_label: vacancyDetails.cover_letter_label || "",
        cover_letter_required: vacancyDetails?.cover_letter_required || null,
        requiresCV:
          vacancyDetails.resume_enable !== undefined
            ? vacancyDetails.resume_enable
            : true,
        resume_label: vacancyDetails.resume_label || "", // Add this line
        resume_required: vacancyDetails?.resume_required || null,
        isPicEnabled: vacancyDetails.is_pic_enable || false,
        sms: vacancyDetails.sms === 1 || vacancyDetails.sms === true,
        foundationMembers:
          vacancyDetails.foundation_members === 1 ||
          vacancyDetails.foundation_members === true,
        foundationMemberOption: vacancyDetails.hotlist_id
          ? String(vacancyDetails.hotlist_id)
          : null,
        formTemplate: vacancyDetails.formTemplate || "",
        startingSalary: vacancyDetails.salary
          ? String(vacancyDetails.salary)
          : "",
        openDate: vacancyDetails.start_date
          ? new Date(vacancyDetails.start_date)
          : null,
        closeDate: vacancyDetails.end_date
          ? new Date(vacancyDetails.end_date)
          : null,
        placesAvailable: vacancyDetails.positions || "",
        customQuestions:
          vacancyDetails.custom_fields?.job?.map((field) => ({
            label: field.label,
            field_type: field.field_type,
            options: field.options,
            is_required: field.is_required,
          })) || [],

        recruiters:
          vacancyDetails?.vacancy_emails?.map((recruiter) => ({
            email: recruiter.email,
          })) ?? [],

        tags:
          vacancyDetails?.vacancy_tag?.map((tag) => ({ title: tag.title })) ||
          [],
        selectedEmailTemplate: vacancyDetails.email_template_id
          ? String(vacancyDetails.email_template_id)
          : "",
        emailCustomization: vacancyDetails.emailCustomization || "",
        preCampaignText: vacancyDetails.preCampaignText || "",
        postApplicationText: vacancyDetails.post_application_message || "",
        campaignEndMessage: vacancyDetails.campaignEndMessage || "",
        customProcess: vacancyDetails.customProcess || false,
        customFields: {
          job: vacancyDetails.custom_fields?.job || [],
          personal: vacancyDetails.custom_fields?.personal || [],
        },

        scorecardOptions: vacancyDetails?.scorecards
          ? vacancyDetails?.scorecards.map((option) => ({
              id: option.id || uuidv4(), // Use the existing ID from API if available
              label: option.skill_name,
              weightage: option.weightage,
            }))
          : [],
        smartRules: vacancyDetails.smart_rules
          ? vacancyDetails.smart_rules.map((rule) => ({
              id: uuidv4(),
              field: rule.label,
              form_type: rule.form_type,
              operator: rule.operator,
              value: rule.value,
            }))
          : [],
        bullhorn_contact_id: String(vacancyDetails.bullhorn_contact_id || ""),
        timezone: vacancyDetails.time_zone || "",
        startTime: vacancyDetails.start_time
          ? vacancyDetails.start_time.substring(0, 5)
          : "", // Convert "HH:MM:SS" to "HH:MM"
        endTime: vacancyDetails.end_time
          ? vacancyDetails.end_time.substring(0, 5)
          : "", // Convert "HH:MM:SS" to "HH:MM"
        aiSummaryPrompt:
          vacancyDetails.ai_summary_prompt ||
          vacancyDetails.aiSummaryPrompt ||
          "",
        aiSummaryFields: vacancyDetails.ai_summary_fields || [],
      
      });
      setCampaign(vacancyDetails.campaign || {});

      // Set the selected color based on the vacancy details
      setSelectedColor(vacancyDetails.color); // Default to red if no color is set
    }
  }, [isEditMode, vacancyDetails]);
  // console.log("sequenceSteps", sequenceSteps);

  useEffect(() => {
    const transformFields = (fields) =>
      fields.map((field) => ({
        ...field,
        is_required: field.is_required === "1",
      }));

    if (isEditMode) {
      console.log("i am here in edit mode");
      setShowJobSpecificFormBuilder(true);
      setCustomProcessEnabled(!!vacancyDetails.vacancy_status);

      const p_OrderStatus =
        vacancyDetails.vacancy_statuses
          ?.filter(
            (status) =>
              status.orignal_status.name !== "Applied" &&
              status.orignal_status.name !== "Rejected",
          )
          .map((status) => ({
            p_order: status.p_order,
            status_id: status.orignal_status.id,
          })) || [];
      setPOrderStatus(p_OrderStatus);
      setSequenceSteps(
        vacancyDetails.vacancy_status
          ?.filter(
            (status) => status.name !== "Applied" && status.name !== "Rejected",
          )
          .map((status) => ({
            id: status.id, // Use the original database ID instead of generated one
            p_order: p_OrderStatus.find((item) => item.status_id === status.id)
              .p_order,
            title: status.name,
            color: status.bg_color || "#FFFFFF",
          })) || [],
      );
      setSelectedPersonalFormForBuilder({
        title: "Personal Form",
        fields: transformFields(vacancyDetails.custom_fields?.personal || []),
      });
      setPersonalFormFields(
        transformFields(vacancyDetails.custom_fields?.personal || []),
      );
      setCampaign(vacancyDetails.campaign || {});

      setSelectedJobSpecificFormForBuilder({
        title: "Job Specific Form",
        fields: transformFields(vacancyDetails.custom_fields?.job || []),
      });
      setJobSpecificFormFields(
        transformFields(vacancyDetails.custom_fields?.job || []),
      );
      setShowPersonalFormBuilder(true);
    }
  }, [vacancyDetails]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
      return;
    }

    // Allow only numbers and commas for the salary field
    if (name === "startingSalary" && !/^[0-9,]*$/.test(value)) {
      return; // Ignore invalid input
    }

    // Special handling for slug field to enforce slug format rules
    if (name === "slug") {
      // Convert to lowercase and replace invalid characters
      let processedValue = generateSlug(value);
      console.log("newSlug", processedValue);

      // let processedValue = value.toLowerCase();

      // // Replace spaces and underscores with dashes
      // processedValue = processedValue.replace(/[\s_]+/g, '-');

      // // Remove special characters except dashes
      // processedValue = processedValue.replace(/[^a-z0-9-]/g, '');

      // // Remove leading and trailing dashes
      // processedValue = processedValue.replace(/^-+|-+$/g, '');

      // // Replace consecutive dashes with single dash
      // processedValue = processedValue.replace(/-+/g, '-');

      // Limit length to prevent extremely long slugs
      if (processedValue.length > 100) {
        processedValue = processedValue.substring(0, 100);
      }

      // Validate the processed slug
      const isValid = validateSlugFormat(processedValue);

      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
        slugValid: isValid,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  const handleTagAdd = () => {
    // console.log("newTag", newTag);
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, { title: newTag.trim() }],
      }));
      setNewTag("");
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };
  useEffect(() => {
    if (isEditMode && vacancyDetails) {
      // Map smart rules with proper field name lookup
      const mappedSmartRules = vacancyDetails.smart_rules
        ? vacancyDetails.smart_rules.map((rule) => {
            // Find the corresponding custom field to get the label
            const personalField = vacancyDetails.custom_fields?.personal?.find(
              (field) => field.id === rule.vacancy_custom_field_id,
            );

            const jobField = vacancyDetails.custom_fields?.job?.find(
              (field) => field.id === rule.vacancy_custom_field_id,
            );

            const field = personalField || jobField;
            const form_type = personalField ? "personal" : "job";

            return {
              id: rule.id || uuidv4(),
              field: field?.label || `Field ID ${rule.vacancy_custom_field_id}`,
              form_type: form_type,
              operator: rule.operator,
              value: rule.value,
            };
          })
        : [];

      setFormData((prev) => ({
        ...prev,
        smartRules: mappedSmartRules,
      }));
    }
  }, [isEditMode, vacancyDetails]);

  // const handleRecruiterAdd = () => {
  //   if (
  //     newRecruiter.trim() &&
  //     !formData.recruiters.includes(newRecruiter.trim())
  //   ) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       recruiters: [...prev.recruiters, { email: newRecruiter.trim() }],
  //     }));
  //     setNewRecruiter("");
  //   }
  // };

  // const handleRecruiterRemove = (recruiter) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     recruiters: prev.recruiters.filter((r) => r !== recruiter),
  //   }));
  // };

  // Updated handlers to add multiple forms
  const handlePersonalFormSelect = (formId) => {
    if (!formId) return;

    // Convert IDs to strings for matching
    const form = personalForms.find(
      (f) => f.id.toString() === formId.toString(),
    );
    if (form) {
      // console.log("Found personal form:", form);
      setSelectedPersonalFormForBuilder(form);
      setShowPersonalFormBuilder(true);
      setPersonalFormPlaceholder(form.title);
    } else {
      console.error("No matching personal form found for", formId);
    }
  };

  const handleJobSpecificFormSelect = (formId) => {
    if (!formId) return;

    // Convert IDs to strings for matching
    const form = jobSpecificForms.find(
      (f) => f.id.toString() === formId.toString(),
    );
    if (form) {
      // console.log("Found job specific form:", form);
      setSelectedJobSpecificFormForBuilder(form);
      setShowJobSpecificFormBuilder(true);
      setJobSpecificFormPlaceholder(form.title);
    } else {
      console.error("No matching job specific form found for", formId);
    }
  };

  const handleRemovePersonalForm = (formId) => {
    setSelectedPersonalForms((prev) => prev.filter((f) => f.id !== formId));
  };

  const handleRemoveJobSpecificForm = (formId) => {
    setSelectedJobSpecificForms((prev) => prev.filter((f) => f.id !== formId));
  };

  const handleDragStart = (e, index) => {
    setDraggedStep(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedStep === null) return;

    const newSteps = [...sequenceSteps];
    const [draggedItem] = newSteps.splice(draggedStep, 1);
    newSteps.splice(dropIndex, 0, draggedItem);

    setSequenceSteps(newSteps);
    setDraggedStep(null);
  };

  const handleDeleteField = (formId, fieldId) => {
    // This function is not used anymore in the combined form approach.
  };

  const handleReorderFields = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    // Reorder logic remains for drag & drop functionality across fields.
  };

  const handleSaveAsDraft = () => {
    saveDraft("vacancy", formData);
  };

  // State for error messages corresponding to inputs
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // Validate necessary fields and date logic.
  const validateForm = () => {
    let validationErrors = {};

    if (!campaign) {
      validationErrors.campaignId = "Campaign selection is required.";
    }
    if (!formData.title.trim()) {
      validationErrors.title = "Vacancy title is required.";
    }
    if (!formData.description.trim()) {
      validationErrors.description = "Vacancy description is required.";
    }
    if (
      !formData.bullhorn_contact_id ||
      !String(formData.bullhorn_contact_id).trim()
    ) {
      validationErrors.bullhorn_contact_id = "Contact ID is required.";
    }
    if (isNaN(Number(formData.startingSalary.replace(/,/g, "")))) {
      validationErrors.startingSalary = "Enter a valid starting salary.";
    }
    if (!formData.campaignId) {
      validationErrors.campaignId = "Campaign selection is required.";
    }

    // Skip date validation if in edit mode
    if (!isEditMode) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const openDate = new Date(formData.openDate);
      openDate.setHours(0, 0, 0, 0);
      const closeDate = new Date(formData.closeDate);
      closeDate.setHours(0, 0, 0, 0);

      if (openDate < today) {
        validationErrors.openDate = "Open date must be in the today or future.";
      }
      if (closeDate <= openDate) {
        validationErrors.closeDate = "Close date must be after open date.";
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Validate Smart Rules fields exist in Personal form
  const validateSmartRulesFields = () => {
    const personalSmartRules = formData.smartRules.filter(
      (rule) => rule.form_type === "personal",
    );

    if (personalSmartRules.length === 0) {
      return { isValid: true, missingFields: [] };
    }

    // Get all available personal form field names
    const personalFieldNames = personalFormFields.map(
      (field) => field.question || field.label,
    );

    // Find smart rule fields that don't exist in personal form
    const missingFields = personalSmartRules
      .filter((rule) => !personalFieldNames.includes(rule.field))
      .map((rule) => rule.field);

    return {
      isValid: missingFields.length === 0,
      missingFields: missingFields,
    };
  };

  // NEW: States to store fields from the two form builders
  const [personalFormFields, setPersonalFormFields] = useState([]);
  const [jobSpecificFormFields, setJobSpecificFormFields] = useState([]);

  // Add this state near other state declarations
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add new state for draft loading
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Add new state variables for scorecard option inputs
  const [newScorecardOption, setNewScorecardOption] = useState({
    label: "",
    weightage: "",
  });

  // Add handler for scorecard option inputs
  const handleScorecardOptionChange = (e) => {
    const { name, value } = e.target;
    setNewScorecardOption((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add handler to add scorecard option
  const handleAddScorecardOption = () => {
    if (
      newScorecardOption.label.trim() &&
      newScorecardOption.weightage.trim()
    ) {
      // Validate weightage is a number between 0-100
      const weightage = parseFloat(newScorecardOption.weightage);
      if (isNaN(weightage) || weightage < 0 || weightage > 100) {
        showToast({
          message: "Weightage must be a number between 0 and 100",
          type: "error",
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        scorecardOptions: [
          ...prev.scorecardOptions,
          {
            id: uuidv4(),
            label: newScorecardOption.label,
            weightage: newScorecardOption.weightage,
            is_new: true,
          },
        ],
      }));
      setNewScorecardOption({ label: "", weightage: "" });
    } else {
      showToast({
        message: "Please enter both label and weightage",
        type: "error",
      });
    }
  };

  // Add handler to remove scorecard option
  const handleRemoveScorecardOption = (id) => {
    setFormData((prev) => ({
      ...prev,
      scorecardOptions: prev.scorecardOptions.filter(
        (option) => option.id !== id,
      ),
    }));
  };

  // Calculate total weightage of all scorecard options
  const totalWeightage = formData.scorecardOptions.reduce(
    (sum, option) => sum + parseFloat(option.weightage || 0),
    0,
  );

  // Add new state for the current rule being created
  const [newSmartRule, setNewSmartRule] = useState({
    field: "",
    form_type: "personal", // Default form type
    operator: "=", // Default operator
    value: "",
  });

  // Add handler for smart rule inputs
  const handleSmartRuleChange = (e) => {
    const { name, value } = e.target;
    setNewSmartRule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add handler to add smart rule
  const handleAddSmartRule = () => {
    if (
      newSmartRule.field &&
      newSmartRule.operator &&
      newSmartRule.value.trim()
    ) {
      // Find which form the selected field belongs to
      const isPersonalField = personalFormFields.some(
        (field) => (field.question || field.label) === newSmartRule.field,
      );

      const formType = isPersonalField ? "personal" : "job";

      setFormData((prev) => ({
        ...prev,
        smartRules: [
          ...prev.smartRules,
          {
            id: uuidv4(),
            field: newSmartRule.field,
            form_type: formType,
            operator: newSmartRule.operator,
            value: newSmartRule.value,
          },
        ],
      }));
      setNewSmartRule({
        field: "",
        form_type: "personal",
        operator: "=",
        value: "",
      });
    } else {
      showToast({
        message:
          "Please fill in all fields for the smart rule (field, operator, and value)",
        type: "error",
      });
    }
  };

  // Add handler to remove smart rule
  const handleRemoveSmartRule = (id) => {
    setFormData((prev) => ({
      ...prev,
      smartRules: prev.smartRules.filter((rule) => rule.id !== id),
    }));
  };

  const handleSubmit = (e, isDraft = false, isPreview = false) => {
    e.preventDefault();
    if (isDraft) {
      setIsSavingDraft(true);
    } else {
      setIsSubmitting(true);
    }

    if (!validateForm()) {
      showToast({
        message: "Please fix validation errors before submitting.",
        type: "error",
      });
      setIsSubmitting(false);
      setIsSavingDraft(false);
      return;
    }

    // Validate Smart Rules fields exist in Personal form (only for non-draft submissions)
    if (!isDraft) {
      const smartRulesValidation = validateSmartRulesFields();
      if (!smartRulesValidation.isValid) {
        const missingFieldsList = smartRulesValidation.missingFields.join(", ");
        showToast({
          message: `Cannot submit form: The following fields exist in Smart Rules but are missing from the Personal form: ${missingFieldsList}. Please remove these fields to the  smart rules or remove them from Smart Rules.`,
          type: "error",
          duration: 8000,
        });
        setIsSubmitting(false);
        setIsSavingDraft(false);
        return;
      }
    }

    // Validate total weightage is 100% if scorecard options are provided
    if (
      formData.scorecardOptions.length > 0 &&
      Math.abs(totalWeightage - 100) > 0.01
    ) {
      showToast({ message: "Total weightage must equal 100%", type: "error" });
      setIsSubmitting(false);
      setIsSavingDraft(false);
      return;
    }

    const formattedStartDate = formatDate(formData.openDate);
    const formattedEndDate = formatDate(formData.closeDate);

    // Transform fields for personal and job specific forms
    // console.log("personalFormFields", personalFormFields);
    // console.log("jobSpecificFormFields", jobSpecificFormFields);

    const payload = preparePayload(formData, campaign, isDraft);

    // Log the payload to the console
    // console.log("Vacancy Payload NEW:", payload);

    if (isEditMode) {
      updateVacancy(
        { vacancyId, vacancyData: payload },
        {
          onSuccess: () => {
            showToast({
              message: "Vacancy updated successfully!",
              type: "success",
            });
            setIsSubmitting(false);
            setIsSavingDraft(false);
            navigate(`/companies/view/${campaign.company_id}`, {
              state: { activeTab: "vacancies" },
            });
          },
          onError: (error) => {
            setIsSubmitting(false);
            setIsSavingDraft(false);
            let errorMsg = "An error occurred while updating vacancy.";

            // Check for error_details first (most specific error message)
            if (error.response?.data?.error_details) {
              errorMsg = error.response.data.error_details;
            }
            // Check for error message
            else if (error.response?.data?.error) {
              errorMsg = error.response.data.error;
            }
            // Check for message field
            else if (error.response?.data?.message) {
              errorMsg = error.response.data.message;
            }
            // Check for errors object (validation errors)
            else if (error.response?.data?.errors) {
              const errorData = error.response.data.errors;
              errorMsg = Object.values(errorData).flat().join(" ");
            }
            // Fallback to error.message
            else if (error.message) {
              errorMsg = error.message;
            }

            showToast({ message: errorMsg, type: "error" });
          },
        },
      );
    } else {
      createVacancy(payload, {
        onSuccess: () => {
          showToast({
            message: `Vacancy ${isDraft ? "saved as draft" : "published"} successfully!`,
            type: "success",
          });
          setIsSubmitting(false);
          setIsSavingDraft(false);
          // navigate(`/companies/view/${campaign.company_id}`);
          navigate(`/companies/view/${campaign.company_id}`, {
            state: { activeTab: "vacancies" },
          });
        },
        onError: (error) => {
          setIsSubmitting(false);
          setIsSavingDraft(false);
          let errorMsg = "An error occurred while creating vacancy.";
          if (error.response && error.response.errors) {
            const errorData = error.response.errors;
            errorMsg = Object.values(errorData).flat().join(" ");
          } else if (error.message) {
            errorMsg = error.message;
          }

          // Delay error notification if needed
          setTimeout(() => {
            showToast({ message: errorMsg, type: "error" });
          }, 2000);
        },
      });
    }
  };

  // Transform the field to the format required by the API
  const transformField = (field, index, formType) => {
    const baseField = {
      id: field.id || null,
      label: field.question || field.label,
      sublabel: field.sublabel || null,
      field_type: field.field_type || field.type,
      options:
        field.options && Array.isArray(field.options)
          ? field.options.join(",")
          : field.options || null,
      placeholder: field.placeholder || null,
      max_length: field.max_length || null,
      is_required: field.is_required ? "1" : "0",
      field_order: String(index + 1),
      status: "1",
      form_type: formType,
      created_at: field.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      has_condition: field.has_condition ? "1" : "0",
      conditions: field.conditions || null,
    };

    // Special handling for university_repeatable fields
    if ((field.field_type || field.type) === "university_repeatable") {
      // Create structured options array
      const structuredOptions = [];

      // Add university field
      if (
        field.fieldLabels?.universityLabel ||
        (field.universityOptions && field.universityOptions.length > 0)
      ) {
        structuredOptions.push({
          label: field.fieldLabels?.universityLabel || "University Name",
          sublabel: "",
          options: field.universityOptions || [],
          field_type: "select",
          placeholder: "",
          is_required: true,
        });
      }

      // Add degree field
      if (
        field.fieldLabels?.degreeLabel ||
        (field.degreeOptions && field.degreeOptions.length > 0)
      ) {
        structuredOptions.push({
          label: field.fieldLabels?.degreeLabel || "Qualification",
          sublabel: "",
          options: field.degreeOptions || [],
          field_type: "select",
          placeholder: "",
          is_required: true,
        });
      }

      // Add field of study as text input
      if (field.fieldLabels?.fieldOfStudyLabel) {
        structuredOptions.push({
          label: field.fieldLabels?.fieldOfStudyLabel || "Subject",
          sublabel: "",
          options: [],
          field_type: "text",
          placeholder: "Enter subject/field of study",
          is_required: true,
        });
      }

      // Add graduation year
      if (field.fieldLabels?.graduationYearLabel) {
        structuredOptions.push({
          label: field.fieldLabels?.graduationYearLabel || "Graduation Year",
          sublabel: "",
          options: [],
          field_type: "number",
          placeholder: "e.g., 2023",
          is_required: true,
        });
      }

      // Add GPA
      if (field.fieldLabels?.gpaLabel) {
        structuredOptions.push({
          label: field.fieldLabels?.gpaLabel || "GPA",
          sublabel: "",
          options: [],
          field_type: "number",
          placeholder: "e.g., 3.8",
          is_required: false,
        });
      }

      // Set structured options as JSON string
      if (structuredOptions.length > 0) {
        baseField.options = JSON.stringify(structuredOptions);
        console.log(
          "VacancyForm - University field structured options:",
          baseField.options,
        );
      }

      // Include additional university-specific fields (keeping only university_options and field_of_study_options)
      if (field.universityOptions && field.universityOptions.length > 0) {
        baseField.university_options = field.universityOptions.join(",");
      }
      if (field.fieldOfStudyOptions && field.fieldOfStudyOptions.length > 0) {
        baseField.field_of_study_options = field.fieldOfStudyOptions.join(",");
      }
    }

    return baseField;
  };

  // Prepare the payload for the API
  const preparePayload = (formData, campaign, isDraft = false) => {
    // Before preparing the payload, format the date according to the format required by the API
    const formattedStartDate = formatDate(formData.openDate);
    const formattedEndDate = formatDate(formData.closeDate);

    // Transform the fields to the format required by the API
    const customFieldsPersonal = personalFormFields.map((field, index) =>
      transformField(field, index, "personal"),
    );
    const customFieldsJobSpecific = jobSpecificFormFields.map((field, index) =>
      transformField(field, index, "job"),
    );

    // Get existing status IDs from vacancy_statuses when updating
    let appliedStatusId = null;
    let rejectedStatusId = null;
    let otherStatusIds = {}; // Map to store other status IDs by their name

    if (isEditMode && vacancyDetails?.vacancy_statuses) {
      // Handle different data structures - check for both direct and nested status objects
      const appliedStatus = vacancyDetails.vacancy_statuses.find(
        (status) =>
          status.status?.name === "Applied" ||
          status.orignal_status?.name === "Applied" ||
          status.name === "Applied",
      );
      const rejectedStatus = vacancyDetails.vacancy_statuses.find(
        (status) =>
          status.status?.name === "Rejected" ||
          status.orignal_status?.name === "Rejected" ||
          status.name === "Rejected",
      );

      // Use the vacancy_statuses ID directly (this is the ID we want)
      appliedStatusId = appliedStatus?.id;
      rejectedStatusId = rejectedStatus?.id;

      // Get IDs for all other statuses
      vacancyDetails.vacancy_statuses.forEach((status) => {
        const statusName =
          status.status?.name || status.orignal_status?.name || status.name;
        if (
          statusName &&
          statusName !== "Applied" &&
          statusName !== "Rejected"
        ) {
          otherStatusIds[statusName] = status.id;
        }
      });
    }

    // Helper function to determine if an ID should be included in payload
    const shouldIncludeId = (id) => {
      // Include ID only if it's a valid numeric ID (not auto-generated like "step-9")
      return id && typeof id === "number" && !isNaN(id);
    };

    // Prepare the vacancy statuses for the payload
    let vacancyStatuses = [
      // Pre-screening step (always first)
      {
        ...(shouldIncludeId(appliedStatusId) && { id: appliedStatusId }), // Include ID only if valid numeric ID
        name: "Applied",
        p_order: 1,
        bg_color: "#4A7263", // Use a color that matches your UI theme
      },
      // User-defined steps (shifted by 1 in p_order)
      ...sequenceSteps.map((step, index) => {
        const statusId = otherStatusIds[step.title] || step.id || null;
        return {
          ...(shouldIncludeId(statusId) && { id: statusId }), // Include ID only if valid numeric ID
          name: step.title,
          p_order: index + 2, // Shift by 1 because Pre-screening is first
          bg_color: step.color || "#FFFFFF",
        };
      }),

      {
        ...(shouldIncludeId(rejectedStatusId) && { id: rejectedStatusId }), // Include ID only if valid numeric ID
        name: "Rejected",
        p_order: sequenceSteps.length + 2, // Add after all user steps
        bg_color: "#DC2626", // Red color for rejected
      },
    ];

    // Debug log to verify payload structure
    console.log("Vacancy Statuses Payload:", vacancyStatuses);

    // Prepare the payload for the API
    const payload = {
      title: formData.title,
      slug: formData.slug,
      slugManual: formData.slugManual,
      description: formData.description,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      start_time: formData.startTime || "",
      end_time: formData.endTime || "",
      time_zone: formData.timezone || "",
      salary: String(formData.startingSalary),
      positions: formData.placesAvailable,
      email_template: formData.selectedEmailTemplate,
      post_application_message: formData.postApplicationText,
      campaign_id: Number(campaign?.id),
      campaign: campaign,
      cover_letter_enable: formData.requiresCoverLetter ? 1 : 0,
      cover_letter_label: formData.cover_letter_label,
      cover_letter_required: formData.cover_letter_required ? 1 : 0,
      resume_enable: formData.requiresCV ? 1 : 0,
      resume_label: formData.resume_label, // Add this line
      resume_required: formData.resume_required ? 1 : 0,
      is_pic_enable: formData.isPicEnabled ? 1 : 0,
      sms: formData.sms ? 1 : 0,
      foundation_members: formData.foundationMembers ? 1 : 0,
      hotlist_id:
        formData.foundationMembers && formData.foundationMemberOption
          ? formData.foundationMemberOption
          : null,
      status: isDraft ? 0 : 1,
      custom_fields: {
        job: customFieldsJobSpecific,
        personal: customFieldsPersonal,
      },

      recruiters_email: formData.recruiters
        .filter(({ email }) => email && email.trim() !== "")
        .map(({ email }) => ({ email: email.trim() })),
      vacancy_tags: formData.tags.map((tag) => ({ title: tag.title })),
      vacancy_statuses: vacancyStatuses,
      scorecard: formData.scorecardOptions.map((option) => {
        if (option?.is_new) {
          return {
            skill_name: option.label,
            weightage: parseFloat(option.weightage),
          };
        } else {
          return {
            id: option.id, // Keep the original ID
            skill_name: option.label,
            weightage: parseFloat(option.weightage),
          };
        }
      }),
      smart_rules: formData.smartRules.map((rule) => ({
        label: rule.field,
        form_type: rule.form_type,
        operator: rule.operator,
        value: rule.value,
      })),
      bullhorn_contact_id: String(formData.bullhorn_contact_id || ""),
      ai_summary_prompt: formData.aiSummaryPrompt || "",
      
    };

    // add ai summary fields to the payload
    if (formData.aiSummaryFields.length > 0) {
      payload.ai_summary_fields = formData.aiSummaryFields;
    }

    return payload;

    // Log the payload to the console
    // console.log("Vacancy Payload NEW:", payload);

    if (isEditMode) {
      updateVacancy(
        { vacancyId, vacancyData: payload },
        {
          onSuccess: (response) => {
            if (isPreview) {
              // Get the vacancy title from form data
              const title = formData.title.toLowerCase().replace(/\s+/g, "-");
              // Open preview in new tab
              window.open(`/${title}`, "_blank");
              showToast({
                message: "Vacancy saved as draft successfully!",
                type: "success",
              });
            } else if (isDraft) {
              showToast({
                message: "Vacancy saved as draft successfully!",
                type: "success",
              });
              navigate(`/companies/view/${campaign.company_id}`, {
                state: { activeTab: "vacancies" },
              });
            } else {
              showToast({
                message: "Vacancy updated successfully!",
                type: "success",
              });
              navigate(`/companies/view/${campaign.company_id}`, {
                state: { activeTab: "vacancies" },
              });
            }
            setIsSubmitting(false);
            setIsSavingDraft(false);
          },
          onError: (error) => {
            setIsSubmitting(false);
            setIsSavingDraft(false);
            let errorMsg = "An error occurred while updating vacancy.";

            // Check for error_details first (most specific error message)
            if (error.response?.data?.error_details) {
              errorMsg = error.response.data.error_details;
            }
            // Check for error message
            else if (error.response?.data?.error) {
              errorMsg = error.response.data.error;
            }
            // Check for message field
            else if (error.response?.data?.message) {
              errorMsg = error.response.data.message;
            }
            // Check for errors object (validation errors)
            else if (error.response?.data?.errors) {
              const errorData = error.response.data.errors;
              errorMsg = Object.values(errorData).flat().join(" ");
            }
            // Fallback to error.message
            else if (error.message) {
              errorMsg = error.message;
            }

            showToast({ message: errorMsg, type: "error" });
          },
        },
      );
    } else {
      createVacancy(payload, {
        onSuccess: (response) => {
          if (isPreview) {
            // Get the vacancy title from form data
            const title = formData.title.toLowerCase().replace(/\s+/g, "-");
            // Open preview in new tab
            window.open(`/${title}`, "_blank");
            showToast({
              message: "Vacancy saved as draft successfully!",
              type: "success",
            });
          } else if (isDraft) {
            showToast({
              message: "Vacancy saved as draft successfully!",
              type: "success",
            });
            navigate(`/companies/view/${campaign.company_id}`, {
              state: { activeTab: "vacancies" },
            });
          } else {
            showToast({
              message: "Vacancy published successfully!",
              type: "success",
            });
            navigate(`/companies/view/${campaign.company_id}`, {
              state: { activeTab: "vacancies" },
            });
          }
          setIsSubmitting(false);
          setIsSavingDraft(false);
        },
        onError: (error) => {
          setIsSubmitting(false);
          setIsSavingDraft(false);
          let errorMsg = "An error occurred while creating vacancy.";
          if (error.response && error.response.errors) {
            const errorData = error.response.errors;
            errorMsg = Object.values(errorData).flat().join(" ");
          } else if (error.message) {
            errorMsg = error.message;
          }

          // Delay error notification if needed
          setTimeout(() => {
            showToast({ message: errorMsg, type: "error" });
          }, 2000);
        },
      });
    }
  };

  const handleAddCustomQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      customQuestions: [
        ...prev.customQuestions,
        { type: "text", question: "" },
      ],
    }));
  };

  const handleCampaignSelect = (campaign) => {
    setFormData((prev) => ({
      ...prev,
      campaignId: campaign.id.toString(),
    }));
    setShowCampaignDropdown(false);
    setCampaignSearch(campaign.name);
  };

  const handleAddStep = () => {
    const newStep = {
      id: `step-${sequenceSteps.length + 2}`,
      title: newStepName,
      p_order: sequenceSteps.length + 2,
      color: selectedColor, // Ensure color is set
    };
    setSequenceSteps((prev) => [...prev, newStep]);
    setNewStepName(""); // Reset input
    setSelectedColor("#FF0000"); // Reset color selection
  };

  const handleDeleteStep = (id) => {
    try {
      // Find the step to delete
      const stepToDelete = sequenceSteps.find((step) => step.id === id);

      if (!stepToDelete) {
        console.warn("Step not found for deletion:", id);
        return;
      }

      // Check if this status has applications associated with it
      // We need to find the corresponding vacancy status from vacancyDetails.vacancy_statuses
      if (isEditMode && vacancyDetails?.vacancy_statuses && stepToDelete) {
        const vacancyStatus = vacancyDetails.vacancy_statuses.find((status) => {
          const statusName =
            status.status?.name || status.orignal_status?.name || status.name;
          return statusName === stepToDelete.title;
        });

        // If status has applications, show confirmation dialog
        if (vacancyStatus && vacancyStatus.applications_count > 0) {
          setStatusToDelete({ id, step: stepToDelete, vacancyStatus });
          setShowDeleteConfirmation(true);
          return;
        }
      }

      // If no applications or not in edit mode, proceed with deletion
      setSequenceSteps((prev) => prev.filter((step) => step.id !== id));
    } catch (error) {
      console.error("Error in handleDeleteStep:", error);
    }
  };

  // Handle confirmed deletion
  const handleConfirmDelete = () => {
    if (statusToDelete) {
      setSequenceSteps((prev) =>
        prev.filter((step) => step.id !== statusToDelete.id),
      );
      setStatusToDelete(null);
    }
    setShowDeleteConfirmation(false);
  };

  // Handle cancel deletion
  const handleCancelDelete = () => {
    setStatusToDelete(null);
    setShowDeleteConfirmation(false);
  };

  const handleMoveStep = (id, direction) => {
    const index = sequenceSteps.findIndex((step) => step.id === id);
    const newSteps = [...sequenceSteps];

    if (direction === "up" && index > 0) {
      const [movedStep] = newSteps.splice(index, 1);
      // console.log("movedStepup", movedStep);
      // movedStep.p_order = movedStep.p_order - 1;
      //upon changing p_order, we need to update the p_order of the other steps and also the p_order of the moved step

      // newSteps.forEach((step, i) => {
      //   step.p_order = i + 1; // Update p_order for all steps including the moved one
      // });
      newSteps.splice(index - 1, 0, movedStep);
      // setSequenceSteps(newSteps);
    } else if (direction === "down" && index < sequenceSteps.length - 1) {
      // const newSteps = [...sequenceSteps];
      const [movedStep] = newSteps.splice(index, 1);
      console.log("movedStepdown", movedStep);
      newSteps.splice(index + 1, 0, movedStep);
    }
    newSteps.forEach((step, i) => {
      step.p_order = i + 2; // Update p_order for all steps including the moved one
    });
    setSequenceSteps(newSteps);
  };

  const handleReorderSteps = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const reorderedSteps = Array.from(sequenceSteps);
    const [movedStep] = reorderedSteps.splice(source.index, 1);
    reorderedSteps.splice(destination.index, 0, movedStep);
    reorderedSteps.forEach((step, i) => {
      step.p_order = i + 2; // Update p_order for all steps including the moved one
    });
    setSequenceSteps(reorderedSteps);
  };

  // Apply template stages when a template is selected
  useEffect(() => {
    // Only apply template stages if a template is explicitly selected
    if (
      selectedTemplateId &&
      selectedTemplate &&
      selectedTemplate.status &&
      Array.isArray(selectedTemplate.status)
    ) {
      const templateStages = selectedTemplate.status
        .filter(
          (status) => status.name !== "Applied" && status.name !== "Rejected",
        )
        .map((status, index) => ({
          id: uuidv4(), // Generate new ID for new stages
          title: status.name,
          color: status.bg_color || "#FF0000",
          p_order: index + 2, // Start from 2 (after Applied)
        }));

      setSequenceSteps(templateStages);
    }
  }, [selectedTemplateId, selectedTemplate]);

  const handleToggleField = (fieldId) => {
    // Not modified in this example.
  };

  const handleEditorChange = (fieldId, content) => {
    // Not modified in this example.
  };

  const handleAddField = (field) => {
    setFormFields((prevFields) => [...prevFields, { ...field, id: uuidv4() }]);
  };

  const handleMoveField = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const reorderedFields = Array.from(formFields);
    const [movedField] = reorderedFields.splice(source.index, 1);
    reorderedFields.splice(destination.index, 0, movedField);
  };

  // Add this validation function
  const validateField = (field) => {
    const newErrors = { ...errors };

    switch (field) {
      case "title":
        if (!formData.title.trim()) {
          newErrors.title = "Vacancy title is required";
        } else {
          delete newErrors.title;
        }
        break;

      case "description":
        if (!formData.description.trim()) {
          newErrors.description = "Vacancy description is required";
        } else {
          delete newErrors.description;
        }
        break;

      case "bullhorn_contact_id":
        if (
          !formData.bullhorn_contact_id ||
          !String(formData.bullhorn_contact_id).trim()
        ) {
          newErrors.bullhorn_contact_id = "Contact ID is required";
        } else {
          delete newErrors.bullhorn_contact_id;
        }
        break;

      case "openDate":
        if (!formData.openDate) {
          newErrors.openDate = "Open date is required";
        } else {
          delete newErrors.openDate;
        }
        break;

      case "closeDate":
        if (!formData.closeDate) {
          newErrors.closeDate = "Close date is required";
        } else if (!formData.openDate) {
          newErrors.closeDate = "Please select open date first";
        } else if (
          new Date(formData.closeDate) <= new Date(formData.openDate)
        ) {
          newErrors.closeDate = "Close date must be after open date";
        } else {
          delete newErrors.closeDate;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !newErrors[field];
  };

  // Add this blur handler
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  // When the mutation is pending, show a full-page Loader.
  // if (isCreatingVacancy || isUpdatingVacancy) {
  //   return <Loader />;
  // }

  const [searchTerm, setSearchTerm] = useState("");

  const getColor = (step) => {
    return step.color || "#FFFFFF";
  };

  // Add new state for editing scorecard option
  const [editingScorecardOption, setEditingScorecardOption] = useState(null);
  const [editingScorecardData, setEditingScorecardData] = useState({
    label: "",
    weightage: "",
  });

  // Add handler to start editing a scorecard option
  const handleStartEditingScorecardOption = (option) => {
    setEditingScorecardOption(option.id);
    setEditingScorecardData({
      label: option.label,
      weightage: option.weightage,
    });
  };

  // Add handler to save edited scorecard option
  const handleSaveEditedScorecardOption = (optionId) => {
    if (!optionId) return;

    // Validate weightage is a number between 0-100
    const weightage = parseFloat(editingScorecardData.weightage);
    if (isNaN(weightage) || weightage < 0 || weightage > 100) {
      showToast({
        message: "Weightage must be a number between 0 and 100",
        type: "error",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      scorecardOptions: prev.scorecardOptions.map((option) =>
        option.id === optionId
          ? {
              ...option,
              label: editingScorecardData.label,
              weightage: editingScorecardData.weightage,
            }
          : option,
      ),
    }));

    setEditingScorecardOption(null);
    setEditingScorecardData({ label: "", weightage: "" });
  };

  // Add handler to cancel editing
  const handleCancelEditingScorecardOption = () => {
    setEditingScorecardOption(null);
    setEditingScorecardData({ label: "", weightage: "" });
  };

  // Add handler for editing scorecard option inputs
  const handleEditingScorecardOptionChange = (e) => {
    const { name, value } = e.target;
    setEditingScorecardData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Define the function to handle navigation back to vacancies
  const handleBackToVacancies = () => {
    if (!campaign) {
      navigate(-1);
      return;
    }
    navigate(`/companies/view/${campaign.company_id}`, {
      state: { activeTab: "vacancies" },
    });
  };

  // Preview handler
  const [previewPayload, setPreviewPayload] = useState(null);
  const handlePreview = () => {
    const payload = preparePayload(formData, campaign, false);
    setPreviewPayload(payload);
    setShowPreviewModal(true);
  };

  // Add handler for AI summary checkbox change
  // const [aiSummaryFields, setAiSummaryFields] = useState([]);
  const handleAISummaryCheckboxChange = (e, field) => {
    if (e.target.checked) {
      setFormData((prev) => ({
        ...prev,
        aiSummaryFields: [...prev.aiSummaryFields, field.label],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        aiSummaryFields: prev.aiSummaryFields.filter((item) => item !== field.label),
      }));
    }
    console.log("aiSummaryFields", formData.aiSummaryFields);
  };

  if (isVacancyLoading) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-card">
        <Loader bgColor="white" loadingText="Loading vacancy details..." />
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleReorderSteps}>
      <div className="flex flex-col min-h-screen bg-background">
        <div className="flex-1 w-[90%] py-8 px-8 mx-auto flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <PageHeader
              navigationText="Back to Vacancies"
              navigateTo={handleBackToVacancies}
              customClasses="!gap-0"
            />
            {/* Header Section */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                    {isEditMode ? "Update Vacancy" : "Create New Vacancy"}
                  </h1>
                  <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                    {isEditMode
                      ? "Modify existing vacancy details and configuration settings."
                      : "Fill in the details below to create and publish a new vacancy."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Tags Section */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Tags</h2>
                  <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                    <Tags className="w-5 h-5 text-foreground" />
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleTagAdd())
                      }
                      className="bg-transparent flex-1 focus:outline-none text-foreground"
                      placeholder="Add a tag..."
                    />
                    <button
                      type="button"
                      onClick={handleTagAdd}
                      className="px-3 py-1 bg-primary-dark text-white rounded-lg hover:brightness-110 transition-colors"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag) => (
                      <span
                        key={tag.title}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-soft text-foreground rounded-full"
                      >
                        {tag.title}
                        <button
                          type="button"
                          onClick={() => handleTagRemove(tag)}
                          className="text-foreground hover:text-foreground"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 !mt-4">
                  <label
                    htmlFor="bullhorn_contact_id"
                    className="block text-foreground font-medium"
                  >
                    Contact ID <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                    <User className="w-5 h-5 text-foreground" />
                    <input
                      type="text"
                      id="bullhorn_contact_id"
                      name="bullhorn_contact_id"
                      value={formData.bullhorn_contact_id || ""}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (touched.bullhorn_contact_id) {
                          validateField("bullhorn_contact_id");
                        }
                      }}
                      onBlur={() => handleBlur("bullhorn_contact_id")}
                      className={`bg-transparent w-full focus:outline-none text-foreground ${errors.bullhorn_contact_id ? "border-b-2 border-red-500" : ""}`}
                      placeholder="Enter bullhorn contact ID"
                      required
                    />
                  </div>
                  {touched.bullhorn_contact_id &&
                    errors.bullhorn_contact_id && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.bullhorn_contact_id}
                      </p>
                    )}
                </div>

                <hr />
                {/* Contact ID Field */}

                {/* General Details */}
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    General Details
                  </h2>

                  <div className="space-y-2">
                    <label
                      htmlFor="title"
                      className="block text-foreground font-medium"
                    >
                      Vacancy Title <span className="text-red-500">*</span>
                    </label>

                    <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                      <Briefcase className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Auto-generate slug from title only if not manually edited
                          if (!formData.slugManual) {
                            const newSlug = generateSlug(e.target.value);
                            const isValid = validateSlugFormat(newSlug);
                            setFormData((prev) => ({
                              ...prev,
                              slug: newSlug,
                              slugValid: isValid,
                              slugAvailable: true, // Assume available for auto-generated slugs
                              slugMessage: "", // Clear message for auto-generated slugs
                            }));
                          }
                          if (touched.title) {
                            validateField("title");
                          }
                        }}
                        onBlur={() => handleBlur("title")}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${
                          errors.title ? "border-b-2 border-red-500" : ""
                        }`}
                        placeholder="Enter vacancy title"
                        required
                      />
                    </div>
                    
                    {touched.title && errors.title && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.title}
                      </p>
                    )}
                  </div>


                  <div className="space-y-2">
                    <label
                      htmlFor="slug"
                      className="block text-foreground font-medium"
                    >
                      Slug <span className="text-muted-foreground text-sm"></span>
                    </label>

                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <LinkIcon className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="slug"
                        name="slug"
                        value={formData.slug}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Mark as manually edited when user changes the slug
                          if (!formData.slugManual) {
                            setFormData((prev) => ({
                              ...prev,
                              slugManual: true,
                            }));
                          }
                          // Call debounced slug availability check when manually entered
                          debouncedSlugCheck(e.target.value);
                        }}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${formData.slugManual ? "border-l-4  pl-2" : ""} ${!formData.slugValid && formData.slug ? "border-b-2 border-red-500" : ""}`}
                        placeholder="Auto-generated from title"
                      />
                      {formData.slugGenerating && (
                        <Loader2 className="w-4 h-4 text-foreground animate-spin" />
                      )}
                      {!formData.slugValid && formData.slug && (
                        <XCircle
                          className="w-4 h-4 text-red-500"
                          title="Invalid slug format"
                        />
                      )}
                      {formData.slugValid &&
                        formData.slug &&
                        !formData.slugGenerating &&
                        !formData.slugAvailable && (
                          <XCircle
                            className="w-4 h-4 text-red-500"
                            title="API validation failed"
                          />
                        )}
                      {formData.slugValid &&
                        formData.slug &&
                        formData.slugAvailable &&
                        !formData.slugGenerating && (
                          <CheckCircle2
                            className="w-4 h-4 text-green-500"
                            title="API validation successful"
                          />
                        )}
                    </div>
                    {!formData.slugValid && formData.slug && (
                      <p className="text-red-500 text-sm mt-1">
                        Slug must contain only lowercase letters, numbers, and
                        single dashes. Cannot start or end with a dash.
                      </p>
                    )}
                    {formData.slugValid &&
                      formData.slug &&
                      !formData.slugAvailable &&
                      formData.slugMessage && (
                        <p className="text-red-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                    {formData.slugValid &&
                      formData.slug &&
                      formData.slugAvailable &&
                      formData.slugMessage && (
                        <p className="text-green-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                    {/* Show message even if slugValid is false but we have a message from API */}
                    {!formData.slugValid &&
                      formData.slug &&
                      formData.slugMessage && (
                        <p className="text-red-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="description"
                      className="block text-foreground font-medium"
                    >
                      Description <span className="text-red-500">*</span>
                    </label>
                    {/* <div className="flex gap-2 bg-muted rounded-lg p-3">
                      <FileEdit className="w-5 h-5 text-foreground mt-1" />
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (touched.description) {
                            validateField("description");
                          }
                        }}
                        onBlur={() => handleBlur("description")}
                        className={`bg-transparent w-full focus:outline-none text-foreground min-h-[200px] resize-none ${errors.description ? "border-b-2 border-red-500" : ""
                          }`}
                        placeholder="Enter vacancy description..."
                        required
                      />
                    </div> */}
                    <div className="border rounded-lg overflow-hidden">
                      {
                        <div className="p-4">
                          <Editor
                            apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                            value={formData.description}
                            onEditorChange={(content) => {
                              setFormData((prev) => ({
                                ...prev,
                                description: content,
                              }));
                            }}
                            init={{
                              height: 300,
                              menubar: false,
                              browser_spellcheck: true,
                              plugins: [
                                "advlist",
                                "autolink",
                                "lists",
                                "link",
                                "image",
                                "charmap",
                                "preview",
                                "anchor",
                                "searchreplace",
                                "visualblocks",
                                "code",
                                "fullscreen",
                                "insertdatetime",
                                "media",
                                "table",
                                "help",
                                "wordcount",
                              ],
                              toolbar:
                                "undo redo | blocks | bold italic forecolor | " +
                                "alignleft aligncenter alignright alignjustify | " +
                                "bullist numlist outdent indent | link image | removeformat | help",
                              content_style:
                                "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
                            }}
                          />
                        </div>
                      }
                    </div>
                    {touched.description && errors.description && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.description}
                      </p>
                    )}
                  </div>
              

                  <div className="space-y-2 relative">
                    <label
                      htmlFor="campaign"
                      className="block text-foreground font-medium"
                    >
                      Campaign <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {isEditMode ? (
                        <input
                          type="text"
                          readOnly
                          value={campaign?.title || "Select a campaign"}
                          className="bg-muted rounded-lg p-3 w-full text-left"
                        />
                      ) : (
                        <Dropdown
                          options={campaigns?.map((campaign) => ({
                            label: campaign.title,
                            value: campaign,
                          }))}
                          value={
                            campaign
                              ? { label: campaign.title, value: campaign }
                              : null
                          }
                          onChange={(selected) => {
                            console.log("Selected campaign:", selected.value);
                            console.log(
                              "Selected campaign company:",
                              selected.value.company?.name,
                            );
                            setCampaign(selected.value);
                            setSelectedCompany(selected.value.company);
                            setFormData((prev) => ({
                              ...prev,
                              campaignId: selected.value.id.toString(),
                            }));
                          }}
                          placeholder="Select a campaign"
                          icon={<Globe className="w-5 h-5 text-foreground" />}
                          searchable={true}
                          className="w-full"
                          dropdownWidth="w-full"
                          // serverUrl=
                        />
                      )}
                    </div>
                    {!campaign && touched.campaignId && (
                      <p className="text-red-500 text-sm mt-1">
                        Campaign selection is required
                      </p>
                    )}
                  </div>

                  {/* <div className="space-y-2 relative">
                    <label
                      htmlFor="campaignField"
                      className="block text-foreground font-medium"
                    >
                      Attach to Campaign
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Globe className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="campaignField"
                        name="campaignField"
                        value={campaign?.title || "Attach campaign"}
                        readOnly
                        className="bg-transparent w-full focus:outline-none text-foreground"
                      />
                    </div>
                  </div> */}
                </div>

                {/* Optional Fields */}
                <div className="space-y-4 bg-muted border border-border p-4 rounded-lg">
                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requiresCoverLetter}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            requiresCoverLetter: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['\00a0'] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                    </label>
                    <span className="text-foreground">Cover Letter Required</span>
                  </div>

                  {/* Add the conditional cover letter label input */}
                  {formData.requiresCoverLetter != "" && (
                    <div className="ml-16 space-y-2">
                      <div className="flex items-center">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-input text-primary-dark accent-primary-dark bg-background"
                            id="coverLetterRequired"
                            name="cover_letter_required"
                            checked={formData.cover_letter_required === 1}
                            onChange={handleInputChange}
                          />
                          <span>Required</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                        <FileText className="w-5 h-5 text-foreground" />
                        <input
                          type="text"
                          id="coverLetterLabel"
                          name="cover_letter_label"
                          value={formData.cover_letter_label}
                          onChange={handleInputChange}
                          className="bg-transparent w-full focus:outline-none text-foreground"
                          placeholder="Enter cover letter instructions "
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requiresCV}
                        onChange={(e) => {
                          console.log("e.target.checked", e.target.checked);
                          setFormData((prev) => ({
                            ...prev,
                            requiresCV: e.target.checked,
                          }));
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['\00a0'] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                    </label>
                    <span className="text-foreground">CV/Resume Required</span>
                  </div>

                  {/* Add the conditional resume label input */}
                  {formData.requiresCV != "" && (
                    <div className="ml-16 space-y-2">
                      <div className="flex items-center">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-input text-primary-dark accent-primary-dark bg-background"
                            id="resumeRequired"
                            name="resume_required"
                            checked={formData.resume_required === 1}
                            onChange={handleInputChange}
                          />
                          <span>Required</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                        <FileText className="w-5 h-5 text-foreground" />
                        <input
                          type="text"
                          id="resumeLabel"
                          name="resume_label"
                          value={formData.resume_label}
                          onChange={handleInputChange}
                          className="bg-transparent w-full focus:outline-none text-foreground"
                          placeholder="Enter CV/resume instructions "
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sms}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sms: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['\00a0'] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                    </label>
                    <span className="text-foreground">Social Mobility Questions</span>
                  </div>


                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.foundationMembers}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            foundationMembers: e.target.checked,
                            foundationMemberOption: e.target.checked
                              ? prev.foundationMemberOption
                              : null,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['\00a0'] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                    </label>
                    <span className="text-foreground">Foundation Members</span>
                  </div>

                  {formData.foundationMembers && (
                    <div className="ml-16">
                      <Dropdown
                        options={hotlistOptions}
                        value={selectedHotlistOption}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            foundationMemberOption: selected?.value ?? null,
                          }))
                        }
                        placeholder="Select hotlist"
                        className="w-full"
                        searchable={true}
                        isLoading={isHotlistLoading}
                        loadingText="Loading hotlists..."
                        disabled={isHotlistLoading || isHotlistError}
                      />
                      {isHotlistError && (
                        <p className="text-red-500 text-sm mt-1">
                          Failed to load hotlists.
                        </p>
                      )}
                    </div>
                  )}

                  
                </div>



                

                <hr className="border-border" />
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Job Details
                  </h2>

                  {/* Personal Details Forms (multiple selection) */}
                  <div className="space-y-4">
                    {/* <h3 className="text-lg font-medium text-foreground">
                      Personal Forms
                    </h3> */}
                    {/* Personal Form Selection */}
                    {!isEditMode && (
                      <div className="space-y-2">
                        <label
                          htmlFor="personalFormSelect"
                          className="block text-foreground font-medium"
                        >
                          Select Personal Form{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                          <select
                            id="personalFormSelect"
                            value={
                              selectedPersonalFormForBuilder
                                ? selectedPersonalFormForBuilder.id?.toString()
                                : ""
                            }
                            onChange={(e) => {
                              if (e.target.value) {
                                handlePersonalFormSelect(e.target.value);
                              }
                            }}
                            className="bg-transparent w-full focus:outline-none text-foreground"
                          >
                            <option value="">{personalFormPlaceholder}</option>
                            {personalForms.map((form) => (
                              <option key={form.id} value={form.id.toString()}>
                                {form.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {/* Conditionally show Personal Form Builder */}
                    {showPersonalFormBuilder &&
                      selectedPersonalFormForBuilder && (
                        <div className="mt-4 border border-brand/20 rounded-lg p-4 bg-muted">
                          <FormBuilderColumns
                            form={selectedPersonalFormForBuilder}
                            onFieldsChange={(fields) => {
                              setPersonalFormFields(fields);
                              console.log(
                                "Updated personal form fields:",
                                fields,
                              );
                            }}
                            smartRules={formData.smartRules.filter(
                              (rule) => rule.form_type === "personal",
                            )}
                          />
                        </div>
                      )}
                  </div>

                  {/* Existing Job Details Fields */}
                  <div className="space-y-2">
                    <label
                      htmlFor="startingSalary"
                      className="block text-foreground font-medium"
                    >
                      Starting Salary
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <PoundSterling className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="startingSalary"
                        name="startingSalary"
                        value={formData.startingSalary}
                        onChange={handleInputChange}
                        className="bg-transparent w-full focus:outline-none text-foreground"
                        placeholder="Enter starting salary"
                      />
                    </div>
                    {errors.startingSalary && (
                      <p className="text-red-500 text-sm">
                        {errors.startingSalary}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {/* Time Zone */}
                    <div className="space-y-2">
                      <label className="block text-foreground font-medium">
                        Time Zone <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        options={TIMEZONES}
                        // onChange={(selected) => setFieldValue(selected.value)}
                        onChange={(selected) => {
                          setFormData((prev) => ({
                            ...prev,
                            timezone: selected.value,
                          }));
                          if (touched.timezone) {
                            validateField("timezone");
                          }
                        }}
                        placeholder="Select Time Zone"
                        value={formData.timezone || ""}
                        searchable={true}
                        triggerClassName={errors.timezone ? "!border-red-500" : ""}
                        // dropdownWidth='w-full max-w-[22rem]'
                      />
                      {touched.timezone && errors.timezone && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.timezone}
                        </p>
                      )}
                    </div>

                    {/* Start Time */}
                    <div className="space-y-2">
                      <label className="block text-foreground font-medium">
                        Start Time <span className="text-red-400">*</span>
                      </label>
                      <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                        <Clock className="w-5 h-5 text-foreground" />
                        <select
                          value={formData.startTime || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              startTime: e.target.value,
                            }));
                            if (touched.startTime) {
                              validateField("startTime");
                            }
                          }}
                          onBlur={() => handleBlur("startTime")}
                          className={`bg-transparent w-full focus:outline-none text-foreground ${
                            errors.startTime ? "border-b-2 border-red-500" : ""
                          }`}
                        >
                          <option value="">Select time</option>
                          {Array.from({ length: 96 }).map((_, index) => {
                            const hours = Math.floor(index / 4);
                            const minutes = (index % 4) * 15;
                            const time = `${hours
                              .toString()
                              .padStart(2, "0")}:${minutes
                              .toString()
                              .padStart(2, "0")}`;
                            return (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {touched.startTime && errors.startTime && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.startTime}
                        </p>
                      )}
                    </div>

                    {/* End Time */}
                    <div className="space-y-2">
                      <label className="block text-foreground font-medium">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                        <Clock className="w-5 h-5 text-foreground" />
                        <select
                          value={formData.endTime || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              endTime: e.target.value,
                            }));
                            if (touched.endTime) {
                              validateField("endTime");
                            }
                          }}
                          onBlur={() => handleBlur("endTime")}
                          className={`bg-transparent w-full focus:outline-none text-foreground ${
                            errors.endTime ? "border-b-2 border-red-500" : ""
                          }`}
                          required
                        >
                          <option value="">Select time</option>
                          {Array.from({ length: 96 }).map((_, index) => {
                            const hours = Math.floor(index / 4);
                            const minutes = (index % 4) * 15;
                            const time = `${hours
                              .toString()
                              .padStart(2, "0")}:${minutes
                              .toString()
                              .padStart(2, "0")}`;
                            return (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {touched.endTime && errors.endTime && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.endTime}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Applications Open and Close Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="openDate"
                        className="block text-foreground font-medium"
                      >
                        Applications Open{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                        <Calendar className="w-5 h-5 text-foreground" />
                        <input
                          type="date"
                          min={
                            formData.openDate ||
                            new Date().toISOString().split("T")[0]
                          }
                          value={
                            formData.openDate
                              ? new Date(formData.openDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              openDate: e.target.value,
                            }));
                            if (touched.openDate) {
                              validateField("openDate");
                            }
                          }}
                          onBlur={() => handleBlur("openDate")}
                          className={`bg-transparent w-full focus:outline-none text-foreground ${
                            errors.openDate ? "border-b-2 border-red-500" : ""
                          }`}
                        />
                      </div>
                      {touched.openDate && errors.openDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.openDate}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="closeDate"
                        className="block text-foreground font-medium"
                      >
                        Applications Close{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2 rounded-lg p-3 bg-muted">
                        <Calendar className="w-5 h-5 text-foreground" />
                        <input
                          type="date"
                          min={
                            formData.openDate
                              ? new Date(
                                  new Date(formData.openDate).getTime() +
                                    86400000,
                                )
                                  .toISOString()
                                  .split("T")[0]
                              : new Date().toISOString().split("T")[0]
                          }
                          value={
                            formData.closeDate
                              ? new Date(formData.closeDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              closeDate: e.target.value,
                            }));
                            if (touched.closeDate) {
                              validateField("closeDate");
                            }
                          }}
                          onBlur={() => handleBlur("closeDate")}
                          className={`bg-transparent w-full focus:outline-none text-foreground ${
                            !formData.openDate
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          } ${errors.closeDate ? "border-b-2 border-red-500" : ""}`}
                          disabled={!formData.openDate}
                          // required
                        />
                      </div>
                      {touched.closeDate && errors.closeDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.closeDate}
                        </p>
                      )}
                      {!formData.openDate && (
                        <p className="text-sm text-foreground">
                          Please select open date first
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="placesAvailable"
                      className="block text-foreground font-medium"
                    >
                      Places Available
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Users className="w-5 h-5 text-foreground" />
                      <input
                        type="number"
                        id="placesAvailable"
                        name="placesAvailable"
                        value={formData.placesAvailable}
                        onChange={handleInputChange}
                        className="bg-transparent w-full focus:outline-none text-foreground"
                        placeholder="Number of positions"
                      />
                    </div>
                  </div>
                </div>




                {/* Job Specific Form Section */}
                <div className="space-y-4">
                  {/* <h3 className="text-lg font-medium text-foreground">
                    Job Specific Forms
                  </h3> */}
                  {!isEditMode && (
                    <div className="space-y-2">
                      <label
                        htmlFor="jobSpecificFormSelect"
                        className="block text-foreground font-medium"
                      >
                        Select Job Specific Form{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                        {!isEditMode && (
                          <select
                            id="jobSpecificFormSelect"
                            value={
                              selectedJobSpecificFormForBuilder
                                ? selectedJobSpecificFormForBuilder.id.toString()
                                : ""
                            }
                            onChange={(e) => {
                              if (e.target.value) {
                                handleJobSpecificFormSelect(e.target.value);
                              }
                            }}
                            className="bg-transparent w-full focus:outline-none text-foreground"
                          >
                            <option value="">
                              {jobSpecificFormPlaceholder}
                            </option>
                            {jobSpecificForms.map((form) => (
                              <option key={form.id} value={form.id.toString()}>
                                {form.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Conditionally show Job Specific Form Builder */}
                  {showJobSpecificFormBuilder &&
                    selectedJobSpecificFormForBuilder && (
                      <div className="mt-4 border border-brand/20 rounded-lg p-4 bg-muted">
                        <FormBuilderColumns
                          form={selectedJobSpecificFormForBuilder}
                          onFieldsChange={(fields) => {
                            setJobSpecificFormFields(fields);
                            console.log(
                              "Updated job specific form fields:",
                              fields,
                            );
                          }}
                          smartRules={formData.smartRules.filter(
                            (rule) => rule.form_type === "job",
                          )}
                        />
                      </div>
                    )}
                </div>

                {/* show all job specific forms fields as isAnyCheckboxChecked */}
                {selectedJobSpecificFormForBuilder && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label
                        htmlFor="aiSummaryFields"
                        className="block text-foreground font-medium"
                    >
                      AI Summary Fields
                    </label>
                    <div>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="selectAllAISummaryFields"
                            checked={formData.aiSummaryFields.length === jobSpecificFormFields?.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData((prev) => ({
                                  ...prev,
                                  aiSummaryFields: jobSpecificFormFields.map(f => f.label),
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  aiSummaryFields: [],
                                }));
                              }
                            }}
                            className="w-4 h-4 text-primary-dark"
                          />
                          <span className="ml-2 text-sm text-primary-dark">Select All</span>
                        </label>
                  
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 border border-brand/20 rounded-lg p-4 bg-muted">
                    {jobSpecificFormFields?.map((field) => (
                      <div className="flex items-start gap-2">
                        <input type="checkbox" id={field.id} onChange={(e) => handleAISummaryCheckboxChange(e, field)} 
                        name={field.id} className="w-4 h-4 mt-1" 
                        checked={formData.aiSummaryFields.includes(field.label)} />
                        <label htmlFor={field.id} key={field.id} className="text-sm text-foreground">
                            {field.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                )}
                {/* ai summary prompt form  */}
                <div className="!mt-4 pt-4 border-t border-border space-y-2">
                  <h3 className="font-medium text-foreground">
                    AI Candidate Summary Prompt
                  </h3>
                  <textarea
                    id="aiSummaryPrompt"
                    name="aiSummaryPrompt"
                    rows={4}
                    value={formData.aiSummaryPrompt}
                    onChange={handleInputChange}
                    className="bg-card border border-border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500/30 text-foreground text-sm"
                    placeholder="please write the text that you want to be included in the AI candidate summary"
                  />
                </div>
                {/* Calendar/Email Options */}
                <div className="space-y-4">
                  {/* <h2 className="text-xl font-semibold text-foreground mb-4">
                    Calendar & Email Options
                  </h2> */}

                  {/* Recruiters Input */}
                  {/* <div className="space-y-2">
                    <label
                      htmlFor="recruiters"
                      className="block text-foreground font-medium"
                    >
                      Recruiters
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <User className="w-5 h-5 text-foreground" />
                      <input
                        type="email"
                        value={newRecruiter}
                        onChange={(e) => setNewRecruiter(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), handleRecruiterAdd())
                        }
                        className="bg-transparent flex-1 focus:outline-none text-foreground"
                        placeholder="Add a recruiter email"
                      />
                      <button
                        type="button"
                        onClick={handleRecruiterAdd}
                        className="px-3 py-1 bg-primary-dark text-white rounded-lg hover:brightness-110"
                      >
                        Add Recruiter
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.recruiters.map((recruiter) => (
                        <span
                          key={recruiter.email}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-soft text-foreground rounded-full"
                        >
                          {recruiter.email}
                          <button
                            type="button"
                            onClick={() => handleRecruiterRemove(recruiter)}
                            className="text-foreground hover:text-foreground"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div> */}

                  {/* Email Template Dropdown */}
                  {/* <div className="space-y-2">
                    <label htmlFor="selectedEmailTemplate" className="block text-foreground font-medium">
                      Email Template
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <FileText className="w-5 h-5 text-foreground" />
                      <select
                        id="selectedEmailTemplate"
                        name="selectedEmailTemplate"
                        value={formData.selectedEmailTemplate}
                        onChange={handleInputChange}
                        className="bg-transparent w-full focus:outline-none text-foreground"
                      >
                        <option value="">Select an email template</option>
                        {emailTemplates.map(template => (
                          <option key={template.id} value={template.id}>{template.name}</option>
                        ))}
                      </select>
                    </div>
                  </div> */}

                  {/* Email Customization */}
                  {/* <div className="space-y-2">
                    <label htmlFor="emailCustomization" className="block text-foreground font-medium">
                      Email Template Customization
                    </label>
                    <div className="flex gap-2 bg-muted rounded-lg p-3">
                      <FileEdit className="w-5 h-5 text-foreground mt-1" />
                      <textarea
                        id="emailCustomization"
                        name="emailCustomization"
                        value={formData.emailCustomization}
                        onChange={handleInputChange}
                        className="bg-transparent w-full focus:outline-none text-foreground min-h-[100px] resize-none"
                        placeholder="Customize your email template..."
                      />
                    </div>
                  </div> */}
                </div>

                {/* Page Elements */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Page Elements
                  </h2>

                  {/* Post-Application Text Dropdown */}
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedPageElement(
                          expandedPageElement === "postApplication"
                            ? null
                            : "postApplication",
                        )
                      }
                      className="w-full flex items-center justify-between p-4 bg-muted hover:bg-secondary-soft dark:hover:bg-accent"
                    >
                      <span className="text-foreground font-medium">
                        Post-Application Thanks Page
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${
                          expandedPageElement === "postApplication"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    </button>
                    {expandedPageElement === "postApplication" && (
                      <div className="p-4">
                        <Editor
                          apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                          value={formData.postApplicationText}
                          onEditorChange={(content) => {
                            setFormData((prev) => ({
                              ...prev,
                              postApplicationText: content,
                            }));
                          }}
                          init={{
                            height: 300,
                            menubar: false,
                            browser_spellcheck: true,
                            plugins: [
                              "advlist",
                              "autolink",
                              "lists",
                              "link",
                              "image",
                              "charmap",
                              "preview",
                              "anchor",
                              "searchreplace",
                              "visualblocks",
                              "code",
                              "fullscreen",
                              "insertdatetime",
                              "media",
                              "table",
                              "help",
                              "wordcount",
                            ],
                            toolbar:
                              "undo redo | blocks | bold italic forecolor | " +
                              "alignleft aligncenter alignright alignjustify | " +
                              "bullist numlist outdent indent | link image | removeformat | help",
                            content_style:
                              "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Auto-Responder Email Dropdown */}
                  {/* <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedPageElement(
                          expandedPageElement === "autoResponder"
                            ? null
                            : "autoResponder"
                        )
                      }
                      className="w-full flex items-center justify-between p-4 bg-muted hover:bg-secondary-soft dark:hover:bg-accent"
                    >
                      <span className="text-foreground font-medium">
                        Auto-Responder Email
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${expandedPageElement === "autoResponder"
                          ? "transform rotate-180"
                          : ""
                          }`}
                      />
                    </button>
                    {expandedPageElement === "autoResponder" && (
                      <div className="p-4">
                        <Editor
                          apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                          value={formData.autoResponderEmail}
                          onEditorChange={(content) => {
                            setFormData((prev) => ({
                              ...prev,
                              autoResponderEmail: content
                            }));
                          }}
                          init={{
                            height: 300,
                            menubar: false,
                            plugins: [
                              'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                              'preview', 'anchor', 'searchreplace', 'visualblocks',
                              'code', 'fullscreen', 'insertdatetime', 'media', 'table',
                              'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | bold italic forecolor | ' +
                              'alignleft aligncenter alignright alignjustify | ' +
                              'bullist numlist outdent indent | link image | removeformat | help',
                            content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }'
                          }}
                        />
                      </div>
                    )}
                  </div> */}
                </div>

                {/* Application Sequence */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Application Sequence
                  </h2>

                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customProcessEnabled}
                          onChange={(e) =>
                            setCustomProcessEnabled(e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['\00a0'] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                      </label>
                      <span className="text-foreground">Enable Custom Process</span>
                    </div>
                    {customProcessEnabled && (
                      <div className="flex items-center gap-2">
                        <Dropdown
                          options={templates.map((template) => ({
                            label: template.title,
                            value: template.id,
                          }))}
                          value={
                            selectedTemplateId
                              ? {
                                  label:
                                    templates.find(
                                      (t) => t.id === selectedTemplateId,
                                    )?.title || "",
                                  value: selectedTemplateId,
                                }
                              : null
                          }
                          onChange={(selected) => {
                            const templateId = selected?.value || null;
                            setSelectedTemplateId(templateId);
                          }}
                          placeholder="Select a template"
                          searchable={true}
                          className="min-w-[200px]"
                        />
                      </div>
                    )}
                  </div>

                  {customProcessEnabled && (
                    <DragDropContext onDragEnd={handleReorderSteps}>
                      <Droppable droppableId="sequenceSteps">
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="space-y-2"
                          >
                            <div className="flex justify-between items-center p-2 border rounded-lg bg-muted">
                              <div className="flex items-center">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: "#4A7263" }} // Fixed green color for Applied
                                />
                                <span className="ml-2">Applied</span>
                              </div>
                            </div>
                            {sequenceSteps
                              .sort((a, b) => a.p_order - b.p_order)
                              .map((step, index) => (
                                <Draggable
                                  key={`draggable-${step.id}`}
                                  draggableId={String(step.id)}
                                  index={index}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="flex justify-between items-center p-2 border rounded-lg bg-muted"
                                    >
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-2 h-2 rounded-full"
                                          style={{
                                            backgroundColor: getColor(step),
                                          }}
                                        />

                                        <input
                                          type="text"
                                          value={step.title}
                                          onChange={(e) => {
                                            const newSteps = [...sequenceSteps];
                                            newSteps[index].title =
                                              e.target.value;
                                            setSequenceSteps(newSteps);
                                          }}
                                          className="bg-transparent focus:outline-none text-foreground"
                                        />
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="color"
                                          value={step.color || "#FFFFFF"}
                                          onChange={(e) => {
                                            const newSteps = [...sequenceSteps];
                                            newSteps[index].color =
                                              e.target.value;
                                            setSequenceSteps(newSteps);
                                          }}
                                          className="appearance-none w-6 h-6 rounded-full cursor-pointer p-0 bg-transparent"
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleMoveStep(step.id, "up")
                                          }
                                          className="text-foreground hover:text-foreground"
                                          disabled={index === 0}
                                        >
                                          <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleMoveStep(step.id, "down")
                                          }
                                          className="text-foreground hover:text-foreground"
                                          disabled={
                                            index === sequenceSteps.length - 1
                                          }
                                        >
                                          <ChevronDown className="w-4 h-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDeleteStep(step.id)
                                          }
                                          className="text-red-500 hover:text-red-700"
                                        >
                                          <Trash2 className="w-5 h-5" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            <div className="flex justify-between items-center p-2 border rounded-lg bg-muted">
                              <div className="flex items-center">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: "#DC2626" }} // Fixed red color for Rejected
                                />
                                <span className="ml-2">Rejected</span>
                              </div>
                            </div>
                            {provided.placeholder}
                            <div className="flex items-center mt-2 gap-4">
                              <input
                                type="text"
                                value={newStepName}
                                onChange={(e) => setNewStepName(e.target.value)}
                                placeholder="Enter step name"
                                className="border rounded-lg p-2 mr-2"
                              />
                              <input
                                type="color"
                                value={selectedColor}
                                onChange={(e) =>
                                  setSelectedColor(e.target.value)
                                }
                                className="appearance-none w-10 h-10 rounded-[50%] cursor-pointer p-0 bg-transparent"
                              />
                              <button
                                onClick={handleAddStep}
                                className="bg-primary-dark hover:brightness-110 transition-colors text-white rounded-lg px-4 py-2"
                                type="button"
                              >
                                Add Step
                              </button>
                            </div>
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>

                {/* Scorecard Options Section - Add this new section */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Scorecard Options
                  </h2>

                  <div className="flex flex-col space-y-4 bg-muted p-4 rounded-lg">
                    {/* Display existing scorecard options */}
                    {formData.scorecardOptions.length > 0 && (
                      <div className="">
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          Current Scorecard Options
                        </h3>
                        <div className="space-y-2">
                          {formData.scorecardOptions.map((option) => (
                            <div
                              key={option.id}
                              className="flex items-center justify-between bg-card p-3 rounded-lg shadow-sm"
                            >
                              {editingScorecardOption === option.id ? (
                                <div className="flex-1 flex items-center gap-4">
                                  <div className="flex-1">
                                    <input
                                      type="text"
                                      name="label"
                                      value={editingScorecardData.label}
                                      onChange={
                                        handleEditingScorecardOptionChange
                                      }
                                      className="bg-transparent w-full focus:outline-none text-foreground border-b border-brand"
                                      placeholder="Enter label"
                                    />
                                  </div>
                                  <div className="w-24">
                                    <input
                                      type="number"
                                      name="weightage"
                                      value={editingScorecardData.weightage}
                                      onChange={
                                        handleEditingScorecardOptionChange
                                      }
                                      className="bg-transparent w-full focus:outline-none text-foreground border-b border-brand"
                                      placeholder="Weightage"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSaveEditedScorecardOption(
                                          option.id,
                                        )
                                      }
                                      className="text-green-500 hover:text-green-700"
                                    >
                                      <CheckCircle2 className="w-5 h-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={
                                        handleCancelEditingScorecardOption
                                      }
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <XCircle className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex-1">
                                    <p className="font-medium text-foreground">
                                      {option.label}
                                    </p>
                                    <p className="text-sm text-[#5ba88c]">
                                      Weightage: {option.weightage}%
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleStartEditingScorecardOption(
                                          option,
                                        )
                                      }
                                      className="text-foreground hover:text-foreground"
                                    >
                                      <PenTool className="w-5 h-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveScorecardOption(option.id)
                                      }
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                        <div
                          className={`mt-2 ml-3 font-medium ${Math.abs(totalWeightage - 100) > 0.01 ? "text-red-500" : "text-green-600"}`}
                        >
                          Total: {totalWeightage.toFixed(1)}%{" "}
                          {Math.abs(totalWeightage - 100) > 0.01 &&
                            "(should be 100%)"}
                        </div>
                      </div>
                    )}

                    {/* Add new scorecard option */}
                    <div className="bg-muted p-4 rounded-lg space-y-3">
                      <h3 className="text-lg font-medium text-foreground">
                        Add Scorecard Option
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-3">
                          <label
                            htmlFor="scorecardLabel"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Label
                          </label>
                          <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                            <ListChecks className="w-5 h-5 text-foreground" />
                            <input
                              type="text"
                              id="scorecardLabel"
                              name="label"
                              value={newScorecardOption.label}
                              onChange={handleScorecardOptionChange}
                              className="bg-transparent w-full focus:outline-none text-foreground"
                              placeholder="e.g., Technical Skills, Communication"
                            />
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="scorecardWeightage"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Weightage (%)
                          </label>
                          <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                            <Tags className="w-5 h-5 text-foreground" />
                            <input
                              type="number"
                              id="scorecardWeightage"
                              name="weightage"
                              value={newScorecardOption.weightage}
                              onChange={handleScorecardOptionChange}
                              className="bg-transparent w-full focus:outline-none text-foreground"
                              placeholder="Weightage (0-100)"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddScorecardOption}
                          className="bg-primary-dark text-white rounded-lg px-4 py-2 hover:brightness-110 transition-colors"
                        >
                          Add Option
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add the new Smart Rules Section here */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Smart Rules
                  </h2>

                  <div className="flex flex-col space-y-4 bg-muted p-4 rounded-lg">
                    {/* Display existing smart rules */}
                    {formData.smartRules.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          Current Smart Rules
                        </h3>
                        <div className="space-y-2">
                          {formData.smartRules.map((rule) => (
                            <div
                              key={rule.id}
                              className="flex items-center justify-between bg-card p-3 rounded-lg shadow-sm"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                      rule.form_type === "personal"
                                        ? "bg-secondary-soft text-foreground"
                                        : "bg-muted text-muted-foreground"
                                    }`}
                                  >
                                    {rule.form_type === "personal"
                                      ? "Personal"
                                      : "Job"}
                                  </span>
                                  <p className="font-medium text-foreground">
                                    {rule.field} {rule.operator} {rule.value}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveSmartRule(rule.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add new smart rule with Dropdown component */}
                    <div className="bg-muted p-4 rounded-lg space-y-3">
                      <h3 className="text-lg font-medium text-foreground">
                        Add Smart Rule
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {/* Field selector dropdown */}
                        <div className="md:col-span-5">
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Field
                          </label>
                          <Dropdown
                            triggerClassName="border-border border-[lightgrey]"
                            options={[
                              ...personalFormFields.map((field) => ({
                                label: field.question || field.label,
                                value: field.question || field.label,
                                category: "personal",
                                type: field.field_type || field.type, // Include field type for rendering
                              })),
                              ...jobSpecificFormFields.map((field) => ({
                                label: field.question || field.label,
                                value: field.question || field.label,
                                category: "job",
                                type: field.field_type || field.type, // Include field type for rendering
                              })),
                            ]}
                            value={
                              newSmartRule.field
                                ? {
                                    label: newSmartRule.field,
                                    value: newSmartRule.field,
                                  }
                                : null
                            }
                            onChange={(option) =>
                              setNewSmartRule((prev) => ({
                                ...prev,
                                field: option.value,
                                form_type: option.category, // This captures 'personal' or 'job' from the option
                              }))
                            }
                            placeholder={
                              <span className="text-md text-muted-foreground">
                                Select a field
                              </span>
                            }
                            icon={
                              <FileQuestion className="w-5 h-5 text-foreground" />
                            }
                            searchable={true}
                            categories={["personal", "job"]}
                            renderOption={(option) => (
                              <div className="flex flex-col">
                                <span className="text-foreground font-medium">
                                  {option.label}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                      option.category === "personal"
                                        ? "bg-secondary-soft text-foreground"
                                        : "bg-muted text-muted-foreground"
                                    }`}
                                  >
                                    {option.category === "personal"
                                      ? "Personal"
                                      : "Job"}
                                  </span>
                                  {option.type && (
                                    <span className="text-xs text-muted-foreground">
                                      Type:{" "}
                                      {option.type.charAt(0).toUpperCase() +
                                        option.type.slice(1)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>

                        {/* Operator dropdown */}
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Operator
                          </label>
                          <Dropdown
                            options={[
                              { label: "=", value: "=" },
                              { label: "≠", value: "!=" },
                              { label: ">", value: ">" },
                              { label: "<", value: "<" },
                              { label: "≥", value: ">=" },
                              { label: "≤", value: "<=" },
                              { label: "Contains", value: "contains" },
                            ]}
                            value={{
                              label: newSmartRule.operator,
                              value: newSmartRule.operator,
                            }}
                            onChange={(option) =>
                              setNewSmartRule((prev) => ({
                                ...prev,
                                operator: option.value,
                              }))
                            }
                            placeholder="Select operator"
                            icon={<Settings className="w-5 h-5 text-foreground" />}
                            triggerClassName="text-xl font-semibold border-[lightgrey]"
                            optionClassName="text-xl font-semibold"
                          />
                        </div>

                        {/* Value input */}
                        <div className="md:col-span-4">
                          <label
                            htmlFor="smartRuleValue"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Value
                          </label>
                          <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                            <FileText className="w-5 h-5 text-foreground" />
                            <input
                              type="text"
                              id="smartRuleValue"
                              name="value"
                              value={newSmartRule.value}
                              onChange={(e) =>
                                setNewSmartRule((prev) => ({
                                  ...prev,
                                  value: e.target.value,
                                }))
                              }
                              className="bg-transparent w-full focus:outline-none text-foreground"
                              placeholder="Enter value"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end mt-2">
                        <button
                          type="button"
                          onClick={handleAddSmartRule}
                          className="bg-primary-dark text-white rounded-lg px-4 py-2 hover:brightness-110 transition-colors"
                        >
                          Add Rule
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={isSavingDraft}
                    className="flex-1 bg-muted text-foreground hover:bg-secondary-soft dark:hover:bg-accent py-4 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden"
                  >
                    {isSavingDraft ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-foreground" />
                        <span className="animate-pulse">Saving Draft...</span>
                      </div>
                    ) : (
                      <>
                        {/* <ArrowLeft className="w-5 h-5" /> */}
                        Save as Draft
                      </>
                    )}
                    {isSavingDraft && (
                      <div className="absolute inset-0 bg-muted opacity-50"></div>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={isSavingDraft}
                    className="flex-1 bg-muted text-foreground hover:bg-secondary-soft dark:hover:bg-accent py-4 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden"
                  >
                    {isSavingDraft ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-foreground" />
                      </div>
                    ) : (
                      <>
                        {/* <ArrowLeft className="w-5 h-5" /> */}
                        Preview
                      </>
                    )}
                    {isSavingDraft && (
                      <div className="absolute inset-0 bg-muted opacity-50"></div>
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-primary-dark hover:brightness-110 text-white py-4 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                        <span className="animate-pulse">
                          {isEditMode ? "Updating..." : "Publishing..."}
                        </span>
                      </div>
                    ) : (
                      <>
                        {/* <Globe className="w-5 h-5" /> */}
                        {isEditMode ? "Update Vacancy" : "Publish Vacancy"}
                      </>
                    )}
                    {isSubmitting && (
                      <div className="absolute inset-0 bg-primary opacity-50"></div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="mt-8 max-w-5xl mx-auto">
          <PageHeader
            navigationText="Back to Vacancies"
            navigateTo={handleBackToVacancies}
          />
        </div>

        {/* Vacancy Preview Modal */}
        <VacancyPreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          vacancyData={previewPayload}
          campaignData={campaign}
          selectedCompany={selectedCompany}
        />

        {/* Confirmation Dialog for Status Deletion */}
        <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Cannot Delete Status"
          message={
            statusToDelete
              ? `The status "${statusToDelete.step?.title}" cannot be deleted because it has ${statusToDelete.vacancyStatus?.applications_count} application(s) associated with it. Please remove all applications from this status before deleting it.`
              : "This status cannot be deleted because it has applications associated with it."
          }
          confirmText="OK"
          cancelText=""
          isDestructive={true}
          showConfirmButton={false}
        />
      </div>
    </DragDropContext>
  );
}

export default VacancyForm;
